import React, { Component } from 'react';
import {
  Container, Row, Col, Card, CardBody, CardTitle,
  Form, FormGroup, Label, Input, Button, Alert,
  Table, Badge, Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap';

export class Settings extends Component {
  static displayName = Settings.name;

  constructor(props) {
    super(props);
    this.state = {
      user: null,
      balance: 0,
      currencies: [],
      defaultCurrency: null,
      loading: true,
      error: null,
      success: null,
      // New currency form
      newCurrencyName: '',
      newCurrencySymbol: '',
      newCurrencyRate: '1.0',
      newCurrencyIsDefault: false,
      // Edit modal
      editModalOpen: false,
      editCurrency: null,
      editName: '',
      editSymbol: '',
      editRate: '',
    };
  }

  componentDidMount() {
    this.loadUser();
  }

  loadUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.setState({ user }, () => {
          this.loadBalance();
          this.loadCurrencies();
        });
      } catch (e) {
        console.error('Error parsing user data:', e);
        this.setState({ loading: false, error: 'Failed to load user data' });
      }
    } else {
      this.setState({ loading: false, error: 'Please login to access settings' });
    }
  }

  async loadBalance() {
    const { user } = this.state;
    if (!user) return;

    try {
      const response = await fetch(`api/auth/user/${user.userId}/balance`);
      if (response.ok) {
        const data = await response.json();
        this.setState({
          balance: data.balance,
          defaultCurrency: data.defaultCurrency
        });
      }
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  }

  async loadCurrencies() {
    const { user } = this.state;
    if (!user) return;

    try {
      const response = await fetch('api/currency', {
        headers: {
          'X-User-Id': user.userId
        }
      });
      if (response.ok) {
        const currencies = await response.json();
        this.setState({ currencies, loading: false });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
      this.setState({ loading: false, error: 'Failed to load currencies' });
    }
  }

  handleCreateCurrency = async (e) => {
    e.preventDefault();
    const { user, newCurrencyName, newCurrencySymbol, newCurrencyRate, newCurrencyIsDefault } = this.state;

    if (!user) return;

    const rate = parseFloat(newCurrencyRate);
    if (isNaN(rate) || rate < 0.001 || rate > 1.0) {
      this.setState({ error: 'Exchange rate must be between 0.001 and 1.0' });
      return;
    }

    try {
      const response = await fetch('api/currency', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.userId
        },
        body: JSON.stringify({
          name: newCurrencyName,
          symbol: newCurrencySymbol,
          exchangeRate: rate,
          isDefault: newCurrencyIsDefault
        })
      });

      if (response.ok) {
        this.setState({
          success: 'Currency created successfully!',
          error: null,
          newCurrencyName: '',
          newCurrencySymbol: '',
          newCurrencyRate: '1.0',
          newCurrencyIsDefault: false
        });
        await this.loadCurrencies();
        await this.loadBalance();
      } else {
        const data = await response.json();
        this.setState({ error: data.message || 'Failed to create currency' });
      }
    } catch (error) {
      console.error('Error creating currency:', error);
      this.setState({ error: 'Failed to create currency' });
    }
  }

  handleDeleteCurrency = async (currencyId) => {
    const { user } = this.state;
    if (!user) return;

    if (!window.confirm('Are you sure you want to delete this currency?')) {
      return;
    }

    try {
      const response = await fetch(`api/currency/${currencyId}`, {
        method: 'DELETE',
        headers: {
          'X-User-Id': user.userId
        }
      });

      if (response.ok) {
        this.setState({ success: 'Currency deleted successfully!', error: null });
        await this.loadCurrencies();
        await this.loadBalance();
      } else {
        this.setState({ error: 'Failed to delete currency' });
      }
    } catch (error) {
      console.error('Error deleting currency:', error);
      this.setState({ error: 'Failed to delete currency' });
    }
  }

  handleSetDefault = async (currencyId) => {
    const { user } = this.state;
    if (!user) return;

    try {
      const response = await fetch(`api/currency/${currencyId}/set-default`, {
        method: 'POST',
        headers: {
          'X-User-Id': user.userId
        }
      });

      if (response.ok) {
        this.setState({ success: 'Default currency updated!', error: null });
        await this.loadCurrencies();
        await this.loadBalance();
      } else {
        this.setState({ error: 'Failed to set default currency' });
      }
    } catch (error) {
      console.error('Error setting default currency:', error);
      this.setState({ error: 'Failed to set default currency' });
    }
  }

  openEditModal = (currency) => {
    this.setState({
      editModalOpen: true,
      editCurrency: currency,
      editName: currency.name,
      editSymbol: currency.symbol,
      editRate: currency.exchangeRate.toString()
    });
  }

  closeEditModal = () => {
    this.setState({
      editModalOpen: false,
      editCurrency: null,
      editName: '',
      editSymbol: '',
      editRate: ''
    });
  }

  handleUpdateCurrency = async () => {
    const { user, editCurrency, editName, editSymbol, editRate } = this.state;
    if (!user || !editCurrency) return;

    const rate = parseFloat(editRate);
    if (isNaN(rate) || rate < 0.001 || rate > 1.0) {
      this.setState({ error: 'Exchange rate must be between 0.001 and 1.0' });
      return;
    }

    try {
      const response = await fetch(`api/currency/${editCurrency.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.userId
        },
        body: JSON.stringify({
          name: editName,
          symbol: editSymbol,
          exchangeRate: rate
        })
      });

      if (response.ok) {
        this.setState({ success: 'Currency updated successfully!', error: null });
        this.closeEditModal();
        await this.loadCurrencies();
      } else {
        const data = await response.json();
        this.setState({ error: data.message || 'Failed to update currency' });
      }
    } catch (error) {
      console.error('Error updating currency:', error);
      this.setState({ error: 'Failed to update currency' });
    }
  }

  formatBalance(balance, currency) {
    if (currency) {
      // Convert USD balance to custom currency
      const converted = balance / currency.exchangeRate;
      return `${currency.symbol}${converted.toFixed(2)}`;
    }
    return `$${balance.toFixed(2)}`;
  }

  render() {
    const {
      user, balance, currencies, defaultCurrency, loading, error, success,
      newCurrencyName, newCurrencySymbol, newCurrencyRate, newCurrencyIsDefault,
      editModalOpen, editName, editSymbol, editRate
    } = this.state;

    if (loading) {
      return <Container><p>Loading...</p></Container>;
    }

    if (!user) {
      return (
        <Container>
          <Alert color="warning">Please login to access settings</Alert>
        </Container>
      );
    }

    // Only customers can manage currencies
    if (user.role !== 0) {
      return (
        <Container>
          <Alert color="info">Currency management is only available for customers</Alert>
        </Container>
      );
    }

    return (
      <Container className="settings-page">
        <div className="text-center mb-4">
          <h1 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
            Your Settings
          </h1>
          <p className="lead" style={{ maxWidth: '500px', margin: '0 auto' }}>
            Manage your currencies and preferences
          </p>
        </div>

        {error && (
          <Alert color="danger" toggle={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" toggle={() => this.setState({ success: null })}>
            {success}
          </Alert>
        )}

        <Row className="mb-4">
          <Col md="6">
            <Card className="balance-display">
              <CardBody className="text-center">
                <CardTitle tag="h4" style={{ color: 'white', fontFamily: 'var(--font-display)', opacity: 0.9 }}>
                  Your Balance
                </CardTitle>
                <div className="balance-amount">{this.formatBalance(balance, defaultCurrency)}</div>
                <p style={{ opacity: 0.8, marginBottom: '0.5rem' }}>USD Equivalent: ${balance.toFixed(2)}</p>
                {defaultCurrency && (
                  <Badge color="light" className="text-dark">
                    Default: {defaultCurrency.name} ({defaultCurrency.symbol})
                  </Badge>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <Card className="mb-4 coffee-card">
              <CardBody>
                <CardTitle tag="h4" style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
                  Create Custom Currency
                </CardTitle>
                <Form onSubmit={this.handleCreateCurrency}>
                  <FormGroup>
                    <Label for="currencyName">Currency Name</Label>
                    <Input
                      type="text"
                      id="currencyName"
                      value={newCurrencyName}
                      onChange={(e) => this.setState({ newCurrencyName: e.target.value })}
                      placeholder="e.g., Coffee Coins"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label for="currencySymbol">Symbol</Label>
                    <Input
                      type="text"
                      id="currencySymbol"
                      value={newCurrencySymbol}
                      onChange={(e) => this.setState({ newCurrencySymbol: e.target.value })}
                      placeholder="e.g., CC"
                      maxLength="5"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label for="exchangeRate">Exchange Rate (1 custom = X USD)</Label>
                    <Input
                      type="number"
                      id="exchangeRate"
                      value={newCurrencyRate}
                      onChange={(e) => this.setState({ newCurrencyRate: e.target.value })}
                      min="0.001"
                      max="1.0"
                      step="0.001"
                      required
                    />
                    <small className="text-muted">
                      Must be between 0.001 and 1.0. Lower rate = more currency needed.
                    </small>
                  </FormGroup>

                  <FormGroup check className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={newCurrencyIsDefault}
                        onChange={(e) => this.setState({ newCurrencyIsDefault: e.target.checked })}
                      />
                      Set as default currency
                    </Label>
                  </FormGroup>

                  <Button color="primary" type="submit">Create Currency</Button>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col md="6">
            <Card className="coffee-card">
              <CardBody>
                <CardTitle tag="h4" style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
                  Your Currencies
                </CardTitle>
                {currencies.length === 0 ? (
                  <p className="text-muted">No custom currencies created yet</p>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Symbol</th>
                        <th>Rate</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currencies.map(currency => (
                        <tr key={currency.id}>
                          <td>
                            {currency.name}
                            {currency.isDefault && (
                              <Badge color="success" className="ms-2">Default</Badge>
                            )}
                          </td>
                          <td>{currency.symbol}</td>
                          <td>{currency.exchangeRate}</td>
                          <td>
                            <Button
                              size="sm"
                              color="info"
                              className="me-1"
                              onClick={() => this.openEditModal(currency)}
                            >
                              Edit
                            </Button>
                            {!currency.isDefault && (
                              <Button
                                size="sm"
                                color="success"
                                className="me-1"
                                onClick={() => this.handleSetDefault(currency.id)}
                              >
                                Set Default
                              </Button>
                            )}
                            <Button
                              size="sm"
                              color="danger"
                              onClick={() => this.handleDeleteCurrency(currency.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Edit Currency Modal */}
        <Modal isOpen={editModalOpen} toggle={this.closeEditModal}>
          <ModalHeader toggle={this.closeEditModal}>Edit Currency</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="editName">Currency Name</Label>
              <Input
                type="text"
                id="editName"
                value={editName}
                onChange={(e) => this.setState({ editName: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label for="editSymbol">Symbol</Label>
              <Input
                type="text"
                id="editSymbol"
                value={editSymbol}
                onChange={(e) => this.setState({ editSymbol: e.target.value })}
                maxLength="5"
              />
            </FormGroup>

            <FormGroup>
              <Label for="editRate">Exchange Rate (1 custom = X USD)</Label>
              <Input
                type="number"
                id="editRate"
                value={editRate}
                onChange={(e) => this.setState({ editRate: e.target.value })}
                min="0.001"
                max="1.0"
                step="0.001"
              />
              <small className="text-muted">
                Must be between 0.001 and 1.0
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.handleUpdateCurrency}>Save Changes</Button>
            <Button color="secondary" onClick={this.closeEditModal}>Cancel</Button>
          </ModalFooter>
        </Modal>
      </Container>
    );
  }
}
